import React, { FC, ReactNode } from "react";
import classNames from "classnames";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { IconAlertGlyph } from "../../../videos/selectedVideoCardV2/panels";
import type { PageMessageKind } from "../pageModel";
import {
  BackButton,
  HEADER_GRID,
  META_CARD,
  META_CARD_STYLE,
  SLOT_BACK,
  SLOT_IDENTITY,
  SLOT_PLATE,
  usePlateSide,
} from "./ProductHeader";
import { SECTION_IDS, SECTION_ORDER } from "./SectionRail";
import {
  CARD_SHADOW,
  SECTION_CARD_CHROME,
  SECTION_TITLE_STYLE,
  STATE_BLOCK_WITH_ACTION,
  StateBlockSkeleton,
} from "./SectionCard";
import { Micro } from "./ui";

/**
 * Page-level states (handoff §7, artboard 7).
 *
 * Both replace behaviour that had no UI: today's page showed a Lottie loader
 * until the header call resolved, and a 404, 403 or 5xx left that loader
 * spinning forever (productDetail/index.tsx:37). No new access check — these
 * are messages for the header call's existing responses (D5).
 */

/** The live skeleton idiom (VID/PageStates.tsx:14-16): --track + pulse, still under reduced motion. */
export const Shimmer: FC<{ className?: string; style?: React.CSSProperties; tint?: boolean }> = ({ className, style, tint }) => (
  <div
    className={classNames(tint ? "bg-tint" : "bg-track", "animate-pulse motion-reduce:animate-none", className)}
    style={style}
    aria-hidden="true"
  />
);

/**
 * A text line's box: the height its real text takes (`lineHeight` × the
 * font size, ProductHeader.tsx), with the bar centred in it.
 */
const LineBox: FC<{ fontSize: number; lineHeight: number; className?: string; children: ReactNode }> = ({
  fontSize,
  lineHeight,
  className,
  children,
}) => (
  <div className={classNames("flex items-center", className)} style={{ fontSize, height: `${lineHeight}em` }}>
    {children}
  </div>
);

/**
 * A section's loading card (SectionCard.tsx): its chrome, the title bar in the
 * h2's line box (SECTION_TITLE_STYLE, 16px × 1.2), then the ScoreState-sized
 * block (StateBlockSkeleton) — the same card the section draws while its own
 * data loads, and the footprint of its most common look (the empty block), so
 * the product landing moves nothing for most products. A plain aria-hidden
 * div, not a SectionCard: no landmark, and no section id for the rail to find.
 */
const CardStandIn: FC<{ titleWidth: number; blockHeight?: number }> = ({ titleWidth, blockHeight }) => (
  <div className={SECTION_CARD_CHROME} style={CARD_SHADOW} aria-hidden="true">
    <LineBox {...SECTION_TITLE_STYLE}>
      <Shimmer className="rounded-full" style={{ width: titleWidth, height: 14 }} />
    </LineBox>
    <div style={{ marginTop: 14 }}>
      <StateBlockSkeleton minHeight={blockHeight} />
    </div>
  </div>
);

// Stand-in widths per section id.
const RAIL_PILL_WIDTH: Record<(typeof SECTION_ORDER)[number], number> = {
  [SECTION_IDS.consumerVoice]: 130,
  [SECTION_IDS.aiPresence]: 110,
  [SECTION_IDS.pages]: 120,
  [SECTION_IDS.videos]: 80,
};
const CARD_TITLE_WIDTH: Record<string, number> = {
  [SECTION_IDS.consumerVoice]: 130,
  [SECTION_IDS.aiPresence]: 110,
  [SECTION_IDS.pages]: 180,
};

interface SkeletonProps {
  onBack?: () => void;
  /** Back's name: "Back to product" while `?video=` is set, as the loaded header's. */
  backLabel?: string;
  /**
   * `?video=` is set: the loaded page is the header and the Videos section
   * showing that video (Q1 b). No rail pills or cards then (the rail and
   * those sections are hidden), and the Videos stand-in is its heading row
   * only — the video view below it brings its own skeleton.
   */
  videoOpen?: boolean;
}

/**
 * Skeleton in the real geometry (07a): the header's own grid slots (plate,
 * back row, title / brand / description bars in their text's line boxes, a
 * meta card with --tint label bars and the role's usual cells), the rail
 * pills, the section strips, and the Videos section as it renders — its
 * heading row (with the brand's Videos | Stories control), toolbar, then the
 * grid (list rows below md, as VideoSection switches). Back is
 * live, as on the video page (VID/PageStates.tsx:71), so it is reachable
 * while this loads; 07a draws it as an empty box. With `?video=` set it draws
 * what that page renders: the header (Back = "Back to product", closing the
 * video) and the Videos heading row, no rail or strips.
 */
export const ProductDetailSkeleton: FC<SkeletonProps> = ({ onBack, backLabel, videoOpen = false }) => {
  const { role } = useUser();
  const internal = isInternal(role);
  // The sections the loaded page shows (SECTION_ORDER, as its rail): one rail
  // pill each, and a card for Consumer Voice and Product pages, so the loaded
  // page doesn't jump up under them. None while a video is open.
  const sections = videoOpen ? [] : SECTION_ORDER;
  // The meta card's usual cells (ProductHeader.tsx): Category and Created,
  // plus Product ID for staff. No Product URL: 325 of the 457 products linked
  // to a brand have none (local DB). So a brand user's card is one row from
  // sm and staff's two, as most products land; the square's side follows the
  // column (usePlateSide), so a closer column moves the text less sideways.
  const metaWidths = internal ? [90, 100, 50] : [90, 100];
  const { gridRef, backRef, identityRef } = usePlateSide<HTMLDivElement>();
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <span className="sr-only" role="status">
        Loading product
      </span>

      {/* Not aria-hidden as a whole: the back button in it is live. */}
      <div ref={gridRef} className={HEADER_GRID}>
        <div ref={backRef} className={SLOT_BACK}>
          {onBack && <BackButton onClick={onBack} label={backLabel} />}
          <Shimmer className="ml-auto w-8 h-8 max-md:w-11 max-md:h-11 rounded-lg shrink-0" />
        </div>
        {/* The photo's footprint: a borderless rounded square, as tall as the
            bars beside it (usePlateSide) — the loaded side whenever the text
            matches the bars; below sm, 200px centred. */}
        <div className={SLOT_PLATE}>
          <Shimmer className="h-full aspect-square max-w-full rounded-xl" />
        </div>
        <div ref={identityRef} className={SLOT_IDENTITY}>
          {/* h1: 24px / 28px from sm, line-height 1.15. */}
          <div className="flex items-center text-[24px] sm:text-[28px]" style={{ height: "1.15em" }}>
            <Shimmer className="rounded-full h-6 sm:h-[28px]" style={{ width: "min(400px, 75%)" }} />
          </div>
          {/* Brand row, then the description: 13.5px, line-height 1.5. The real
              one is 0–2 lines (clamped), so any count moves something. From sm
              it is ONE line, as 07a draws it; below sm, two. Each is the smaller
              shift over the catalogue (local DB, 6,854 products: 651 have none;
              3,314 are under 90 characters, one line at 1440; 4,681 are 50 or
              more, two lines at 375). */}
          <LineBox fontSize={13.5} lineHeight={1.5}>
            <Shimmer className="rounded-full" style={{ width: "min(330px, 60%)", height: 12 }} />
          </LineBox>
          <div>
            <LineBox fontSize={13.5} lineHeight={1.5}>
              <Shimmer className="rounded-full" style={{ width: "min(420px, 90%)", height: 12 }} />
            </LineBox>
            <LineBox fontSize={13.5} lineHeight={1.5} className="sm:hidden">
              <Shimmer className="rounded-full" style={{ width: "45%", height: 12 }} />
            </LineBox>
          </div>
          <div className={META_CARD} style={META_CARD_STYLE}>
            {metaWidths.map((w, i) => (
              <div key={i} className="min-w-0">
                <LineBox fontSize={9.5} lineHeight={1.5}>
                  <Shimmer tint className="rounded-full" style={{ width: i % 2 ? 80 : 60, height: 8 }} />
                </LineBox>
                <LineBox fontSize={13.5} lineHeight={1.5} className="mt-2">
                  <Shimmer className="rounded-full" style={{ width: `min(${w}px, 90%)`, height: 10 }} />
                </LineBox>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* No row at all without pills: an empty item would still take a stack gap. */}
      {sections.length > 0 && (
        <div className="flex gap-2 overflow-hidden" aria-hidden="true">
          {sections.map((id) => (
            <Shimmer key={id} className="rounded-full h-7 max-md:h-10 shrink-0" style={{ width: RAIL_PILL_WIDTH[id] }} />
          ))}
        </div>
      )}

      {sections
        .filter((id) => CARD_TITLE_WIDTH[id] != null)
        .map((id) => (
          <CardStandIn
            key={id}
            titleWidth={CARD_TITLE_WIDTH[id]}
            // Product pages' empty block carries the Add a page button.
            blockHeight={id === SECTION_IDS.pages ? STATE_BLOCK_WITH_ACTION : undefined}
          />
        ))}

      <div aria-hidden="true">
        {/* The heading row (VideosSection.tsx): "Videos" in the h2's line box
            (SECTION_TITLE_STYLE, 16px × 1.2), and for brand users the Videos |
            Stories tabs on the right (SegmentedTabs: 38px, 50px below md with
            its 44px tabs). No count pill: it joins once the list has loaded. */}
        <div className="flex items-center gap-3">
          <LineBox {...SECTION_TITLE_STYLE}>
            <Shimmer className="rounded-full" style={{ width: 60, height: 14 }} />
          </LineBox>
          {!internal && <Shimmer className="ml-auto rounded-[9px] shrink-0 h-[38px] max-md:h-[50px]" style={{ width: 134 }} />}
        </div>
        {!videoOpen && (
          <div className="mt-4 flex flex-col gap-[18px]">
            <div className="flex flex-wrap gap-2.5">
              {[175, 80, 130, 120].map((w, i) => (
                <Shimmer key={i} className="rounded-[10px] h-10" style={{ width: w }} />
              ))}
            </div>
            {/* No count row: this page passes hideCount (ProductVideoList.tsx), so
                the list's row only appears for filter chips or once the brand's
                Non-project probe answers — neither is there on first paint. */}
            <div className="hidden md:grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
              {[0, 1, 2, 3].map((i) => (
                <Shimmer key={i} className="rounded-xl" style={{ height: 320 }} />
              ))}
            </div>
            <div className="md:hidden flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <Shimmer key={i} className="rounded-xl" style={{ height: 96 }} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface MessageProps {
  kind: PageMessageKind;
  onBack?: () => void;
  onRetry?: () => void;
  /** Printed under the block so a bad link can be reported verbatim (as the live `?video=` line). */
  productId?: number;
}

// The handoff's copy (README §7), with its micro-labels.
const COPY: Record<PageMessageKind, { micro: string; title: string; body: string }> = {
  notfound: {
    micro: "Product not found",
    title: "We couldn't find this product",
    body: "It may have been deleted, or the link is wrong.",
  },
  forbidden: {
    micro: "No access",
    title: "This product isn't in your workspace",
    body: "You don't have access to this product.",
  },
  error: {
    micro: "Couldn't load",
    title: "This page didn't load",
    body: "Something went wrong on our end.",
  },
};

/**
 * The three full-page messages, in the live DetailNotFound look
 * (VID/PageStates.tsx:134-182): centred card, tint icon, --surface-2 Go back,
 * outlined Try again, no gold. Buttons are 44px below md. The section card's
 * surface and CARD_SHADOW, but not SECTION_CARD_CHROME: its `sm:p-5` would
 * outrank this card's px-10 py-16 from sm up.
 */
export const ProductPageMessage: FC<MessageProps> = ({ kind, onBack, onRetry, productId }) => {
  const copy = COPY[kind];
  return (
    <div
      className="bg-surface border border-border-token rounded-xl flex flex-col items-center justify-center text-center gap-2.5 px-10 py-16"
      style={{ minHeight: 420, ...CARD_SHADOW }}
    >
      <span className="w-14 h-14 rounded-full bg-tint text-accent-text inline-flex items-center justify-center">
        <IconAlertGlyph size={26} strokeWidth={1.6} />
      </span>
      <Micro className="mt-1">{copy.micro}</Micro>
      <h1 className="m-0 font-CircularStdBlack text-text-1" style={{ fontSize: 28, lineHeight: 1.15 }}>
        {copy.title}
      </h1>
      <p className="m-0 text-text-2" style={{ fontSize: 13.5, lineHeight: 1.5, maxWidth: 420 }}>
        {copy.body}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2.5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center h-10 max-md:h-11 px-4 rounded-xl bg-surface-2 text-text-1 hover:bg-track transition-colors"
            style={{ fontSize: 13.5 }}
          >
            Go back
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center h-10 max-md:h-11 px-4 rounded-xl bg-surface border border-track text-text-1 hover:border-muted transition-colors"
            style={{ fontSize: 13.5 }}
          >
            Try again
          </button>
        )}
      </div>
      {productId != null && (
        <div className="text-text-2 tabular-nums" style={{ fontSize: 11.5, marginTop: 4 }}>
          ?productId={productId}
        </div>
      )}
    </div>
  );
};
